import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Search,
  Loader2,
  UserCircle,
  MoreVertical,
  Shield,
  UserX,
  Key,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { TeamMemberModal } from '@/components/settings/team-member-modal';
import { ResetPasswordModal } from '@/components/settings/reset-password-modal';
import { useIsAdmin } from '@/components/guards/permission-guard';

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  SALES_MANAGER: 'Sales Manager',
  AGENT: 'Agent',
  ACCOUNTANT: 'Accountant',
  OPERATIONS: 'Operations',
  VIEWER: 'Viewer',
};

const roleColors: Record<string, string> = {
  SUPER_ADMIN: 'bg-red-100 text-red-700',
  ADMIN: 'bg-purple-100 text-purple-700',
  SALES_MANAGER: 'bg-blue-100 text-blue-700',
  AGENT: 'bg-green-100 text-green-700',
  ACCOUNTANT: 'bg-orange-100 text-orange-700',
  OPERATIONS: 'bg-cyan-100 text-cyan-700',
  VIEWER: 'bg-gray-100 text-gray-700',
};

export function TeamSettingsPage() {
  const isAdmin = useIsAdmin();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, error } = trpc.settings.listTeamMembers.useQuery({
    page: 1,
    search: searchQuery || undefined,
  });

  const utils = trpc.useUtils();

  const toggleActiveMutation = trpc.settings.updateTeamMember.useMutation({
    onSuccess: () => {
      utils.settings.listTeamMembers.invalidate();
    },
  });

  const handleToggleActive = (id: string, isActive: boolean) => {
    toggleActiveMutation.mutate({ id, isActive: !isActive });
  };

  const handleResetPassword = (member: { id: string; firstName: string; lastName: string }) => {
    setSelectedMember({ id: member.id, name: `${member.firstName} ${member.lastName}` });
    setShowResetPasswordModal(true);
  };

  const users = data?.users ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/settings">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Team Members</h1>
          <p className="text-muted-foreground">Manage staff accounts and permissions</p>
        </div>
        {isAdmin && (
          <Button className="h-8" onClick={() => setShowMemberModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Member
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search team members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-8 w-full border border-border bg-background py-2 pl-10 pr-4 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading team members...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">Error loading team members</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Team Members Grid */}
      {!isLoading && !error && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {users.map((user) => (
            <div
              key={user.id}
              className={`border border-border bg-card p-6 transition-all ${
                !user.isActive ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center bg-primary/10">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="h-10 w-10 object-cover"
                      />
                    ) : (
                      <UserCircle className="h-6 w-6 text-primary" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                {isAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                        <Key className="mr-2 h-4 w-4" />
                        Reset Password
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleToggleActive(user.id, user.isActive)}
                        className={user.isActive ? 'text-red-600' : 'text-green-600'}
                      >
                        {user.isActive ? (
                          <>
                            <UserX className="mr-2 h-4 w-4" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Shield className="mr-2 h-4 w-4" />
                            Activate
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between">
                <span
                  className={`px-2.5 py-0.5 text-xs font-medium ${
                    roleColors[user.role] || 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {roleLabels[user.role] || user.role}
                </span>
                {!user.isActive && (
                  <span className="text-xs text-red-600">Inactive</span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 border-t pt-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Bookings</p>
                  <p className="font-medium">{user._count.bookings}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Quotes</p>
                  <p className="font-medium">{user._count.quotes}</p>
                </div>
              </div>

              {user.lastLoginAt && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Last login: {new Date(user.lastLoginAt).toLocaleDateString()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && users.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <UserCircle className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">No team members found</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {searchQuery ? 'Try adjusting your search' : 'Add your first team member'}
          </p>
          {!searchQuery && isAdmin && (
            <Button className="mt-4 h-8" onClick={() => setShowMemberModal(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Member
            </Button>
          )}
        </div>
      )}

      {/* Modals */}
      {showMemberModal && (
        <TeamMemberModal
          open={showMemberModal}
          onOpenChange={setShowMemberModal}
          onSuccess={() => {
            utils.settings.listTeamMembers.invalidate();
          }}
        />
      )}

      {showResetPasswordModal && selectedMember && (
        <ResetPasswordModal
          open={showResetPasswordModal}
          onOpenChange={setShowResetPasswordModal}
          memberId={selectedMember.id}
          memberName={selectedMember.name}
        />
      )}
    </div>
  );
}
